import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Stepper,
  Step,
  StepLabel,
  CircularProgress,
  Alert,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Checkbox,
  IconButton,
  Card,
  CardContent,
  Chip,
  Divider,
  Tooltip,
} from '@mui/material';
import {
  Close as CloseIcon,
  Description as FormIcon,
  Psychology as AIIcon,
  Preview as PreviewIcon,
  CloudDownload as ImportIcon,
  CheckCircle as CheckCircleIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import { useTeacher } from '../../../context/TeacherContext';
import apiAxiosInstance, { aiAxiosInstance } from '../../../utils/axiosInstance';

interface GoogleForm {
  id: string;
  title: string;
  description?: string;
  createdDate: string;
  questionCount: number;
  questions?: Array<{
    id: string;
    question: string;
    type: string;
    answers?: string[];
  }>;
}

interface ImportedQuestion {
  id: string;
  question: string;
  type: 'MULTIPLE_CHOICE' | 'SHORT_ANSWER' | 'PARAGRAPH_TEXT';
  answers?: string[];
  correctAnswer?: string;
  suggestedStandards?: string[];
  confidence?: number;
  reasoning?: string;
  explanation?: string;
  bloomsLevel?: string;
  wasCorrected?: boolean;
  originalQuestion?: string;
}

interface ImportedTest {
  title: string;
  description?: string;
  questions: ImportedQuestion[];
  originalFormId: string;
  passageId?: string;
  answerKey: string;
}

interface GoogleFormsImportDialogProps {
  open: boolean;
  onClose: () => void;
  onImport: (importedData: ImportedTest) => void;
  gradeLevel: string;
  teacherStandards?: string[];
  state?: string;
}

const steps = ['Select Google Form', 'AI Standards Analysis', 'Review & Import'];

const GoogleFormsImportDialog: React.FC<GoogleFormsImportDialogProps> = ({
  open,
  onClose,
  onImport,
  gradeLevel,
  teacherStandards,
  state
}) => {
  const { teacher } = useTeacher();
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Step 1: Form Selection
  const [forms, setForms] = useState<GoogleForm[]>([]);
  const [selectedForm, setSelectedForm] = useState<GoogleForm | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  
  // Step 2: AI Analysis
  const [importedTest, setImportedTest] = useState<ImportedTest | null>(null);
  const [analysisProgress, setAnalysisProgress] = useState(0);
  
  // Step 3: Review
  const [selectedQuestions, setSelectedQuestions] = useState<string[]>([]);
  
  // Questions Preview Dialog
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [previewForm, setPreviewForm] = useState<GoogleForm | null>(null);

  useEffect(() => {
    if (open && activeStep === 0) {
      setError(null); // Clear any previous errors when dialog opens
      checkGoogleAuth();
    }
  }, [open, activeStep]);

  const checkGoogleAuth = async () => {
    setLoading(true);
    setError(null); // Clear any previous errors
    try {
      const response = await apiAxiosInstance.get('/api/google-forms/auth/status');
      setIsAuthenticated(response.data.authenticated);
      
      if (response.data.authenticated) {
        await loadGoogleForms();
      }
    } catch (err) {
      console.error('Auth check failed:', err);
      setError('Failed to check Google authentication status');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleAuth = async () => {
    setLoading(true);
    try {
      const response = await apiAxiosInstance.get('/api/google-forms/auth/url');
      window.location.href = response.data.authUrl;
    } catch (err) {
      console.error('Google auth failed:', err);
      setError('Failed to initiate Google authentication');
      setLoading(false);
    }
  };

  const loadGoogleForms = async () => {
    setLoading(true);
    setError(null); // Clear any previous errors
    try {
      const response = await apiAxiosInstance.get('/api/google-forms/list');
      console.log('Google Forms API response:', response.data);
      console.log('Forms data:', response.data.forms);
      const formsData = response.data.forms || [];
      console.log('Number of forms:', formsData.length);
      formsData.forEach((form: any, index: number) => {
        console.log(`Form ${index + 1}:`, {
          id: form.id,
          title: form.title,
          questionCount: form.questionCount,
          hasQuestions: !!form.questions,
          questionsLength: form.questions?.length || 0
        });
      });
      setForms(formsData);
      // Don't set error if API call succeeds, even if no forms are found
      // The UI will show "No Google Forms found" message instead
    } catch (err) {
      console.error('Failed to load forms:', err);
      setError('Failed to load Google Forms');
    } finally {
      setLoading(false);
    }
  };

  const handleFormSelection = (form: GoogleForm) => {
    setSelectedForm(form);
  };

  const handleFormUnselection = () => {
    setSelectedForm(null);
  };

  const handlePreviewQuestions = (form: GoogleForm) => {
    console.log('=== handlePreviewQuestions called ===');
    console.log('Form data:', form);
    console.log('Questions array:', form.questions);
    console.log('Questions length:', form.questions?.length);
    setPreviewForm(form);
    setPreviewDialogOpen(true);
    console.log('Preview dialog should be open now');
  };

  const handleNext = async () => {
    if (activeStep === 0) {
      await importAndAnalyzeForm();
    } else if (activeStep === 1) {
      if (importedTest) {
        setSelectedQuestions(importedTest.questions.map(q => q.id));
      }
      setActiveStep(prev => prev + 1);
    } else if (activeStep === 2) {
      await handleCompleteImport();
    }
  };

  const importAndAnalyzeForm = async () => {
    if (!selectedForm) return;
    
    setLoading(true);
    setActiveStep(1);
    setAnalysisProgress(0);
    
    try {
      // Step 1: Import form data
      setAnalysisProgress(25);
      const importResponse = await apiAxiosInstance.get(`/api/google-forms/import/${selectedForm.id}`);
      const formData = importResponse.data;
      
      // Determine whether a real passage exists
      const importedPassageText = (formData.passageText || formData.description || '').trim();
      const hasPassage = !!formData.hasPassage && importedPassageText.length > 0;
      
      // Step 2: Analyze with AI
      setAnalysisProgress(50);
      
      // Prepare teacher standards data - get from teacher's profile
      let standardsArray: string[] = [];
      
      // Get standards from teacher's profile preferences
      if (teacher?.subjectPreferences?.standardsSystem) {
        standardsArray = [teacher.subjectPreferences.standardsSystem];
      }
      
      // If no standards from teacher profile, fall back to props
      if (standardsArray.length === 0 && teacherStandards && Array.isArray(teacherStandards)) {
        standardsArray = teacherStandards.map((standard: any) => {
          if (typeof standard === 'string') {
            return standard;
          } else if (standard && typeof standard === 'object') {
            return standard.name || standard.description || standard.standard || standard.code || String(standard);
          }
          return String(standard);
        }).filter(Boolean);
      }
      
      console.log('Teacher profile standards:', teacher?.teachingStandards);
      console.log('Props teacherStandards:', teacherStandards);
      console.log('Final standardsArray for AI:', standardsArray);
      console.log('Sending to AI backend:', {
        questions: formData.questions.length,
        teacherStandards: standardsArray,
        gradeLevel,
        state: state || teacher?.state
      });
      
      // Calculate appropriate timeout based on number of questions
      // Base timeout of 60 seconds + 5 seconds per question (minimum 120 seconds for large forms)
      const questionCount = formData.questions.length;
      const timeoutMs = Math.max(120000, 60000 + (questionCount * 5000)); // At least 2 minutes, +5s per question
      
      console.log(`Processing ${questionCount} questions with ${timeoutMs/1000}s timeout`);
      
      // Prepare passage content for answer key generation
      let passageContent = '';
      if (hasPassage) {
        passageContent = importedPassageText;
      } else if (formData.description && formData.description.trim()) {
        passageContent = formData.description;
      } else {
        // No passage exists: do not synthesize one for AI; keep empty
        passageContent = '';
      }

      const analysisResponse = await aiAxiosInstance.post('/ai/analyze-standards', {
        questions: formData.questions,
        teacherStandards: standardsArray,
        gradeLevel: gradeLevel,
        state: state || teacher?.state,
        passageContent: passageContent, // Include passage content for answer key generation
        formTitle: formData.title,
        // Subject Configuration - defaults to ELA/Reading for current app focus
        primarySubject: teacher?.primarySubject || "ELA",
        focusSubject: teacher?.subjectPreferences?.focusSubject || "Reading", 
        analysisMode: teacher?.subjectPreferences?.analysisMode || "reading-comprehension",
        standardsSystem: teacher?.subjectPreferences?.standardsSystem || "TEKS"
      }, {
        timeout: timeoutMs, // Dynamic timeout based on question count
        onUploadProgress: () => {
          // Show progress for large forms
          if (questionCount > 10) {
            setAnalysisProgress(60);
          }
        }
      });
      
      setAnalysisProgress(75);
      
      // Combine form data with AI analysis including answer keys
      const analyzedQuestions: ImportedQuestion[] = formData.questions.map((q: any, index: number) => {
        const analysis = analysisResponse.data.analyzedQuestions[index];
        return {
          ...q,
          id: `imported-${index}`,
          suggestedStandards: analysis?.suggestedStandards || [],
          confidence: analysis?.confidence || 0,
          reasoning: analysis?.reasoning || '',
          correctAnswer: analysis?.correctAnswer || '', // Add AI-generated correct answer
          explanation: analysis?.explanation || '',    // Add AI-generated explanation
          bloomsLevel: 'Application', // Default Bloom's level for imported questions
          wasCorrected: q.wasCorrected || false, // Preserve AI correction flag from backend
          originalQuestion: q.originalQuestion // Preserve original question if corrected
        };
      });
      
      setImportedTest({
        title: formData.title,
        description: hasPassage ? importedPassageText : '',
        questions: analyzedQuestions,
        originalFormId: selectedForm.id,
        answerKey: analysisResponse.data.answerKey || ''
      });
      
      setAnalysisProgress(100);
    } catch (err: any) {
      console.error('Import/analysis failed:', err);
      
      // Provide better error messages for different scenarios
      let errorMessage = 'Failed to import and analyze Google Form';
      
      if (err.code === 'ECONNABORTED' || err.message?.includes('timeout')) {
        const questionCount = selectedForm?.questionCount || 'unknown number of';
        errorMessage = `Analysis timed out for form with ${questionCount} questions. Large forms with many questions may take longer to process. Please try again or consider importing a smaller form.`;
      } else if (err.response?.status === 429) {
        errorMessage = 'Too many requests. Please wait a moment and try again.';
      } else if (err.response?.status >= 500) {
        errorMessage = 'AI analysis service is temporarily unavailable. Please try again in a few minutes.';
      } else if (err.response?.data?.message) {
        errorMessage = `Analysis failed: ${err.response.data.message}`;
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleCompleteImport = async () => {
    if (importedTest && selectedQuestions.length > 0) {
      try {
        setLoading(true);
        setError(null);
        
        // Filter selected questions
        const finalQuestions = importedTest.questions.filter(q => selectedQuestions.includes(q.id));
        
        // If there is no passage, only import questions; do not create passage
        const hasRealPassage = !!(importedTest.description && importedTest.description.trim());
        if (!hasRealPassage) {
          // Return only questions to parent without creating an assessment passage
          const finalTest = {
            ...importedTest,
            questions: finalQuestions,
            passageId: undefined
          } as any;
          onImport(finalTest);
          onClose();
          return;
        }
        
        // Create assessment passage when a real passage exists
        const passageContent = importedTest.description!.trim();
        
        console.log('Created passage content:', passageContent);
        
        const passageData = {
          title: importedTest.title,
          passage: passageContent,
          genre: 'Assessment',
          gradeLevel: gradeLevel,
          isAIGenerated: false,
          includeAnswerKey: false,
          answerKey: '',
          isPairedPassage: false,
          passages: [{
            title: importedTest.title,
            passage: passageContent,
            questions: finalQuestions.map(q => ({
              question: q.question,
              answers: q.answers || [],
              correctAnswer: q.correctAnswer || '',
              explanation: q.reasoning || '',
              standardReference: q.suggestedStandards ? q.suggestedStandards.join(', ') : '',
              bloomsLevel: ''
            })),
            genre: 'Assessment',
            gradeLevel: gradeLevel
          }],
          questions: finalQuestions.map(q => ({
            question: q.question,
            answers: q.answers || [],
            correctAnswer: q.correctAnswer || '',
            explanation: q.reasoning || '',
            standardReference: q.suggestedStandards ? q.suggestedStandards.join(', ') : '',
            bloomsLevel: ''
          })),
          standards: finalQuestions
            .flatMap(q => q.suggestedStandards || [])
            .filter((standard, index, self) => self.indexOf(standard) === index)
            .map(standardCode => ({
              id: '',
              code: standardCode,
              standard: standardCode,
              description: ''
            })),
          standardCodes: finalQuestions
            .flatMap(q => q.suggestedStandards || [])
            .filter((standard, index, self) => self.indexOf(standard) === index)
        };
        
        console.log('Creating assessment passage with exact format:', passageData);
        
        const passageResponse = await apiAxiosInstance.post('/api/assessment-passages', passageData);
        const passageId = passageResponse.data.id;
        
        console.log('Created passage with ID:', passageId);
        
        if (passageId && importedTest.answerKey) {
          const updateData = {
            answerKey: importedTest.answerKey,
            includeAnswerKey: true,
            questions: finalQuestions.map(q => ({
              question: q.question,
              answers: q.answers || [],
              correctAnswer: q.correctAnswer || '',
              explanation: q.explanation || q.reasoning || '',
              standardReference: q.suggestedStandards ? q.suggestedStandards.join(', ') : '',
              bloomsLevel: q.bloomsLevel || 'Application'
            }))
          };
          
          console.log('Updating passage with answer key:', updateData);
          await apiAxiosInstance.put(`/api/assessment-passages/${passageId}`, updateData);
        }
        
        const finalTest: ImportedTest = {
          ...importedTest,
          questions: finalQuestions,
          passageId: passageId
        };
        
        onImport(finalTest);
        onClose();
        
      } catch (error: any) {
        console.error('Error creating assessment passage:', error);
        let errorMessage = 'Failed to save imported questions. Please try again.';
        if (error.response?.status === 401) {
          errorMessage = 'Authentication required. Please log in again.';
        } else if (error.response?.status === 403) {
          errorMessage = 'Permission denied. Please check your account access.';
        } else if (error.response?.data?.message) {
          errorMessage = `Error: ${error.response.data.message}`;
        }
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    }
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return renderFormSelection();
      case 1:
        return renderAIAnalysis();
      case 2:
        return renderReviewStep();
      default:
        return null;
    }
  };

  const renderFormSelection = () => (
    <Box>
      {!isAuthenticated ? (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="h6" gutterBottom>
            Connect to Google Forms
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            You need to authenticate with Google to access your forms
          </Typography>
          <Button
            variant="contained"
            size="large"
            onClick={handleGoogleAuth}
            disabled={loading}
            sx={{ 
              backgroundColor: '#4285f4',
              '&:hover': {
                backgroundColor: '#3367d6',
                transform: 'translateY(-2px)',
                boxShadow: '0 6px 16px rgba(0,0,0,0.15), 0 3px 6px rgba(0,0,0,0.10)',
              }
            }}
          >
            {loading ? <CircularProgress size={24} color="inherit" /> : 'Connect Google Account'}
          </Button>
        </Box>
      ) : (
        <Box>
          <Typography variant="h6" gutterBottom>
            Select a Google Form to Import
          </Typography>
          
          {/* Debug test button */}
          <Button 
            variant="outlined" 
            size="small" 
            onClick={() => {
              console.log('Test button clicked');
              const mockForm: GoogleForm = {
                id: 'test-form',
                title: 'Test Form',
                description: 'A test form for debugging',
                createdDate: new Date().toISOString(),
                questionCount: 2,
                questions: [
                  {
                    id: 'q1',
                    question: 'What is your favorite color?',
                    type: 'MULTIPLE_CHOICE',
                    answers: ['Red', 'Blue', 'Green', 'Yellow']
                  },
                  {
                    id: 'q2',
                    question: 'Tell us about yourself',
                    type: 'PARAGRAPH_TEXT',
                    answers: []
                  }
                ]
              };
              handlePreviewQuestions(mockForm);
            }}
            sx={{ mb: 2 }}
          >
            Test Preview Dialog
          </Button>
          
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : forms.length === 0 ? (
            <Alert severity="info">
              No Google Forms found in your account.
            </Alert>
          ) : (
            <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
              {forms.map((form) => (
                <Card
                  key={form.id}
                  sx={{
                    mb: 2,
                    border: selectedForm?.id === form.id ? '2px solid' : '1px solid',
                    borderColor: selectedForm?.id === form.id ? 'primary.main' : 'neutral.cardBorder',
                    cursor: 'pointer',
                    backgroundColor: selectedForm?.id === form.id ? 'primary.light' : 'background.paper',
                    color: selectedForm?.id === form.id ? 'primary.contrastText' : 'text.primary',
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      boxShadow: '0 4px 16px rgba(0,0,0,0.10), 0 2px 6px rgba(0,0,0,0.06)',
                      borderColor: selectedForm?.id === form.id ? 'primary.main' : 'primary.light',
                      borderWidth: selectedForm?.id === form.id ? '2px' : '1px',
                      transform: 'translateY(-2px)',
                      backgroundColor: selectedForm?.id === form.id ? 'primary.light' : 'primary.main',
                      color: selectedForm?.id === form.id ? 'primary.contrastText' : 'primary.contrastText',
                      '& .MuiTypography-root': {
                        color: 'inherit',
                      },
                      '& .MuiChip-root': {
                        backgroundColor: selectedForm?.id === form.id ? 'primary.contrastText' : 'rgba(255,255,255,0.2)',
                        color: selectedForm?.id === form.id ? 'primary.main' : 'primary.contrastText',
                        borderColor: selectedForm?.id === form.id ? 'primary.main' : 'rgba(255,255,255,0.3)',
                      }
                    }
                  }}
                  onClick={() => handleFormSelection(form)}
                >
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="h6" gutterBottom>
                          <FormIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                          {form.title}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          {form.description || 'No description'}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
                          <Chip 
                            label={`${form.questionCount} questions`}
                            size="small"
                            color="primary"
                            variant="outlined"
                            sx={{
                              backgroundColor: selectedForm?.id === form.id ? 'primary.contrastText' : 'transparent',
                              color: selectedForm?.id === form.id ? 'primary.main' : 'primary.main',
                              borderColor: selectedForm?.id === form.id ? 'primary.main' : 'primary.main',
                              transition: 'all 0.2s ease-in-out',
                            }}
                          />
                          <Chip 
                            label={`Created ${new Date(form.createdDate).toLocaleDateString()}`}
                            size="small"
                            variant="outlined"
                            sx={{
                              backgroundColor: selectedForm?.id === form.id ? 'primary.contrastText' : 'transparent',
                              color: selectedForm?.id === form.id ? 'text.secondary' : 'text.secondary',
                              borderColor: selectedForm?.id === form.id ? 'text.secondary' : 'text.secondary',
                              transition: 'all 0.2s ease-in-out',
                            }}
                          />
                        </Box>
                      </Box>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, ml: 2 }}>
                        <Button
                          variant={selectedForm?.id === form.id ? "contained" : "outlined"}
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (selectedForm?.id === form.id) {
                              handleFormUnselection();
                            } else {
                              handleFormSelection(form);
                            }
                          }}
                          sx={{ 
                            borderColor: selectedForm?.id === form.id ? 'error.main' : 'primary.main',
                            color: selectedForm?.id === form.id ? 'error.contrastText' : 'primary.main',
                            backgroundColor: selectedForm?.id === form.id ? 'error.main' : 'transparent',
                            transition: 'all 0.2s ease-in-out',
                            '&:hover': {
                              borderColor: selectedForm?.id === form.id ? 'error.dark' : 'primary.main',
                              backgroundColor: selectedForm?.id === form.id ? 'error.dark' : 'primary.main',
                              color: selectedForm?.id === form.id ? 'error.contrastText' : 'primary.contrastText',
                              transform: 'scale(1.05)',
                            }
                          }}
                        >
                          {selectedForm?.id === form.id ? 'Unselect' : 'Select'}
                        </Button>
                        
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<PreviewIcon />}
                          onClick={(e) => {
                            e.stopPropagation();
                            console.log('Preview button clicked for form:', form);
                            console.log('Form questions:', form.questions);
                            handlePreviewQuestions(form);
                          }}
                          sx={{ 
                            borderColor: selectedForm?.id === form.id ? 'primary.contrastText' : 'primary.main',
                            color: selectedForm?.id === form.id ? 'primary.contrastText' : 'primary.main',
                            backgroundColor: 'transparent',
                            transition: 'all 0.2s ease-in-out',
                            '&:hover': {
                              borderColor: selectedForm?.id === form.id ? 'primary.contrastText' : 'primary.contrastText',
                              backgroundColor: selectedForm?.id === form.id ? 'primary.contrastText' : 'primary.contrastText',
                              color: selectedForm?.id === form.id ? 'primary.main' : 'primary.main',
                              transform: 'scale(1.05)',
                            }
                          }}
                        >
                          Preview {form.questions ? `(${form.questions.length})` : '(0)'}
                        </Button>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </Box>
          )}
        </Box>
      )}
    </Box>
  );

  const renderAIAnalysis = () => (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <AIIcon sx={{ mr: 1, color: 'primary.main' }} />
        <Typography variant="h6">
          AI Standards Analysis
        </Typography>
      </Box>
      
      {loading ? (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <CircularProgress size={60} />
          <Typography variant="body1" sx={{ mt: 2 }}>
            Analyzing questions and mapping to standards...
          </Typography>
          <Box sx={{ width: '100%', mt: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Progress: {analysisProgress}%
            </Typography>
            {selectedForm && selectedForm.questionCount > 10 && (
              <Alert severity="info" sx={{ mt: 2, textAlign: 'left' }}>
                <Typography variant="body2">
                  <strong>Processing {selectedForm.questionCount} questions...</strong>
                </Typography>
                <Typography variant="caption" display="block">
                  Large forms may take 2-4 minutes to analyze. Please wait for the process to complete.
                </Typography>
              </Alert>
            )}
          </Box>
        </Box>
      ) : importedTest ? (
        <Box>
          <Alert severity="success" sx={{ mb: 2 }}>
            Successfully imported "{importedTest.title}" with {importedTest.questions.length} questions
          </Alert>
          
          <Typography variant="subtitle1" gutterBottom>
            Standards Analysis Results:
          </Typography>
          
          <List sx={{ maxHeight: 300, overflow: 'auto' }}>
            {importedTest.questions.map((question, index) => (
              <ListItem key={question.id}>
                <Card sx={{ width: '100%' }}>
                  <CardContent>
                    <Typography variant="body1" gutterBottom>
                      {question.question}
                    </Typography>
                    
                    {question.suggestedStandards && question.suggestedStandards.length > 0 ? (
                      <Box sx={{ mt: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                          Suggested Standards:
                        </Typography>
                        <Box sx={{ mt: 1 }}>
                          {question.suggestedStandards.map((standard, idx) => (
                            <Chip
                              key={idx}
                              label={standard}
                              size="small"
                              color="primary"
                              variant="outlined"
                              sx={{ mr: 0.5, mb: 0.5 }}
                            />
                          ))}
                        </Box>
                        {question.confidence && (
                          <Typography variant="caption" color="text.secondary">
                            Confidence: {Math.round(question.confidence * 100)}%
                          </Typography>
                        )}
                      </Box>
                    ) : (
                      <Alert severity="warning" sx={{ mt: 1 }}>
                        No standards could be automatically mapped for this question
                      </Alert>
                    )}
                  </CardContent>
                </Card>
              </ListItem>
            ))}
          </List>
        </Box>
      ) : null}
    </Box>
  );

  const renderReviewStep = () => (
    <Box>
      <Typography variant="h6" gutterBottom>
        Review Import
      </Typography>
      
      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body2">
          <strong>What will happen:</strong>
        </Typography>
        <Typography variant="body2" component="div" sx={{ mt: 1 }}>
          • Create a new assessment passage titled "{importedTest?.title}"<br/>
          • Add {selectedQuestions.length} selected questions to the passage<br/>
          • Automatically add this passage to your current test<br/>
          • Apply AI-suggested standards to questions
        </Typography>
      </Alert>

      {importedTest && (
        <Box>
          <Typography variant="subtitle1" gutterBottom>
            Form: {importedTest.title}
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            {importedTest.description || 'No description'}
          </Typography>
          
          <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
            Selected Questions ({selectedQuestions.length} of {importedTest.questions.length}):
          </Typography>
          
          <List sx={{ maxHeight: 400, overflow: 'auto', bgcolor: 'background.paper', borderRadius: 1 }}>
            {importedTest.questions.map((question, index) => (
              <ListItem
                key={question.id}
                sx={{
                  opacity: selectedQuestions.includes(question.id) ? 1 : 0.5,
                  bgcolor: selectedQuestions.includes(question.id) ? 'primary.light' : 'transparent'
                }}
              >
                <ListItemIcon>
                  <Checkbox
                    checked={selectedQuestions.includes(question.id)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedQuestions(prev => [...prev, question.id]);
                      } else {
                        setSelectedQuestions(prev => prev.filter(id => id !== question.id));
                      }
                    }}
                  />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, flexDirection: 'column' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <Typography variant="body2">
                          Q{index + 1}: {question.question}
                        </Typography>
                        {question.wasCorrected && (
                          <Tooltip title="This question was corrected by AI to separate passage from question">
                            <Chip 
                              label="AI Corrected" 
                              size="small" 
                              color="info" 
                              icon={<AIIcon />}
                              sx={{ height: 20, fontSize: '0.7rem' }}
                            />
                          </Tooltip>
                        )}
                        {question.confidence !== undefined && question.confidence < 0.6 && (
                          <Tooltip title="Lower confidence - please review this question">
                            <Chip 
                              label="Low Confidence" 
                              size="small" 
                              color="warning" 
                              icon={<InfoIcon />}
                              sx={{ height: 20, fontSize: '0.7rem' }}
                            />
                          </Tooltip>
                        )}
                      </Box>
                    </Box>
                  }
                  secondary={
                    question.suggestedStandards && question.suggestedStandards.length > 0
                      ? `Standards: ${question.suggestedStandards.join(', ')}`
                      : 'No standards mapped'
                  }
                />
              </ListItem>
            ))}
          </List>
          
          <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
            <Button
              size="small"
              onClick={() => setSelectedQuestions(importedTest.questions.map(q => q.id))}
            >
              Select All
            </Button>
            <Button
              size="small"
              onClick={() => setSelectedQuestions([])}
            >
              Deselect All
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );

  const canProceed = () => {
    switch (activeStep) {
      case 0:
        return selectedForm !== null;
      case 1:
        return importedTest !== null && !loading;
      case 2:
        return selectedQuestions.length > 0;
      default:
        return false;
    }
  };

  return (
    <>
      {/* Questions Preview Dialog */}
      <Dialog
        open={previewDialogOpen}
        onClose={() => setPreviewDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { 
            borderRadius: 2,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12), 0 4px 16px rgba(0,0,0,0.08)',
          }
        }}
      >
        <DialogTitle sx={{ 
          backgroundColor: 'primary.main', 
          color: 'primary.contrastText',
          borderRadius: '8px 8px 0 0'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6">
              Preview Questions: {previewForm?.title}
            </Typography>
            <IconButton 
              onClick={() => setPreviewDialogOpen(false)}
              sx={{ color: 'primary.contrastText' }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {previewForm?.questions && previewForm.questions.length > 0 ? (
            <List sx={{ maxHeight: 500, overflow: 'auto' }}>
              {previewForm.questions.map((question, index) => (
                <Card 
                  key={question.id} 
                  sx={{ 
                    mb: 2,
                    border: '1px solid',
                    borderColor: 'neutral.cardBorder',
                    borderRadius: 2,
                    transition: 'all 0.2s ease-in-out',
                    overflow: 'visible',
                    '&:hover': {
                      boxShadow: '0 4px 16px rgba(0,0,0,0.10), 0 2px 6px rgba(0,0,0,0.06)',
                      borderColor: 'primary.main',
                      transform: 'translateY(-1px)',
                      '& .MuiCardContent-root': {
                        backgroundColor: 'background.paper',
                      }
                    }
                  }}
                >
                  <CardContent>
                    <Typography variant="subtitle1" gutterBottom color="primary.main" fontWeight={600}>
                      Question {index + 1}
                    </Typography>
                    <Typography variant="body1" gutterBottom sx={{ lineHeight: 1.6 }}>
                      {question.question}
                    </Typography>
                    
                    <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                      <Chip 
                        label={question.type}
                        size="small"
                        color="secondary"
                        variant="outlined"
                        sx={{
                          borderRadius: 1,
                          fontWeight: 500
                        }}
                      />
                    </Box>
                    
                    {question.answers && question.answers.length > 0 && (
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="body2" color="text.secondary" gutterBottom fontWeight={500}>
                          Answer Options:
                        </Typography>
                        <List dense>
                          {question.answers.map((answer, answerIndex) => (
                            <ListItem 
                              key={answerIndex} 
                              sx={{ 
                                py: 0.5,
                                pl: 3,
                                borderLeft: '3px solid',
                                borderLeftColor: 'primary.light',
                                ml: 1,
                                borderRadius: '0 4px 4px 0',
                                backgroundColor: 'background.default'
                              }}
                            >
                              <ListItemText 
                                primary={`${String.fromCharCode(65 + answerIndex)}. ${answer}`}
                                sx={{ 
                                  '& .MuiListItemText-primary': {
                                    fontSize: '0.9rem',
                                    lineHeight: 1.5
                                  }
                                }}
                              />
                            </ListItem>
                          ))}
                        </List>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              ))}
            </List>
          ) : (
            <Alert 
              severity="info"
              sx={{
                borderRadius: 2,
                backgroundColor: 'info.light',
                '& .MuiAlert-icon': {
                  color: 'info.main'
                }
              }}
            >
              No questions found in this form.
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3, backgroundColor: 'background.default' }}>
          <Button 
            onClick={() => setPreviewDialogOpen(false)}
            sx={{
              color: 'text.secondary',
              '&:hover': {
                backgroundColor: 'neutral.surface',
              }
            }}
          >
            Close
          </Button>
          <Button 
            variant="contained"
            onClick={() => {
              if (previewForm) {
                handleFormSelection(previewForm);
                setPreviewDialogOpen(false);
              }
            }}
            sx={{
              background: 'linear-gradient(45deg, primary.main 30%, primary.light 90%)',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: '0 6px 16px rgba(0,0,0,0.15), 0 3px 6px rgba(0,0,0,0.10)',
              }
            }}
          >
            Select This Assessment
          </Button>
        </DialogActions>
      </Dialog>

      {/* Main Import Dialog */}
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { minHeight: '600px' }
        }}
      >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6">
            Import from Google Forms
          </Typography>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      
      <DialogContent>
        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
        
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        
        {renderStepContent()}
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose}>
          Cancel
        </Button>
        <Button
          onClick={() => setActiveStep(prev => prev - 1)}
          disabled={activeStep === 0}
        >
          Back
        </Button>
        <Button
          onClick={handleNext}
          variant="contained"
          disabled={!canProceed() || loading}
          startIcon={activeStep === 2 ? <ImportIcon /> : undefined}
        >
          {loading ? (
            <CircularProgress size={20} />
          ) : activeStep === 2 ? (
            'Import Test'
          ) : (
            'Next'
          )}
        </Button>
      </DialogActions>
    </Dialog>
    </>
  );
};

export default GoogleFormsImportDialog; 